import React, { useState, useEffect, useCallback } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSpinner, faCheckCircle, faExclamationTriangle, faExclamationCircle } from "@fortawesome/free-solid-svg-icons";
import {
  parseSessionIds,
  fetchBatches,
  createBatch,
  appendBatchItems,
} from "./skillsProfileApi";
import "./skillsProfile.css";

export const ItemSelection = () => {
  const [rawText, setRawText] = useState("");
  const [parsedResult, setParsedResult] = useState(null);
  const [batches, setBatches] = useState([]);
  const [loadingBatches, setLoadingBatches] = useState(true);
  const [batchTarget, setBatchTarget] = useState("existing");
  const [selectedBatchId, setSelectedBatchId] = useState("");
  const [newBatchName, setNewBatchName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [feedback, setFeedback] = useState(null);

  const loadBatches = useCallback(async () => {
    try {
      setLoadingBatches(true);
      const data = await fetchBatches();
      setBatches(data);
      if (data.length > 0 && !selectedBatchId) {
        setSelectedBatchId(data[0].id);
      } else if (data.length === 0) {
        setBatchTarget("new");
      }
    } catch (err) {
      setFeedback({
        type: "danger",
        message: `Failed to load batches: ${err?.detail || err?.message || "Unknown error"}`,
      });
    } finally {
      setLoadingBatches(false);
    }
  }, [selectedBatchId]);

  useEffect(() => {
    loadBatches();
  }, [loadBatches]);

  const handleParse = () => {
    const res = parseSessionIds(rawText);
    setParsedResult(res);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!parsedResult || parsedResult.validUnique.length === 0) return;

    setSubmitting(true);
    setFeedback(null);

    try {
      let targetBatchId = selectedBatchId;
      let targetBatchName = "";

      if (batchTarget === "new") {
        const trimmedName = newBatchName.trim();
        if (!trimmedName) {
          setFeedback({
            type: "danger",
            message: "Please enter a name for the new batch.",
          });
          setSubmitting(false);
          return;
        }
        const created = await createBatch({ name: trimmedName });
        targetBatchId = created.id;
        targetBatchName = created.name;
      } else {
        if (!selectedBatchId) {
          setFeedback({
            type: "danger",
            message: "Please select an existing batch.",
          });
          setSubmitting(false);
          return;
        }
        const found = batches.find((b) => b.id === selectedBatchId);
        targetBatchName = found ? found.name : "Selected batch";
      }

      const res = await appendBatchItems(targetBatchId, parsedResult.validUnique);

      const invalidList = res?.invalid_session_ids || [];
      const dupList = res?.duplicate_session_ids || [];
      const added = res?.added_count ?? 0;
      const total = res?.total_sessions ?? 0;

      const warnings = [];
      if (invalidList.length > 0) {
        warnings.push(`${invalidList.length} session ID(s) rejected as unknown or invalid: ${invalidList.join(", ")}`);
      }
      if (dupList.length > 0) {
        warnings.push(`${dupList.length} session ID(s) already existed in this batch: ${dupList.join(", ")}`);
      }

      setFeedback({
        type: warnings.length > 0 ? "warning" : "success",
        message: `Added ${added} session(s) to "${targetBatchName}". Total batch sessions: ${total}.`,
        details: warnings,
      });

      if (batchTarget === "new") {
        setSelectedBatchId(targetBatchId);
        setBatchTarget("existing");
        setNewBatchName("");
      }
      setRawText("");
      setParsedResult(null);
    } catch (err) {
      setFeedback({
        type: "danger",
        message: `Failed to add sessions: ${err?.detail || err?.message || "Server error"}`,
      });
    } finally {
      try {
        const updatedBatches = await fetchBatches();
        setBatches(updatedBatches);
      } catch (e) {
        // Ignore background refresh failure or log
      }
      setSubmitting(false);
    }
  };

  const parsedCount = parsedResult?.parsedCount ?? 0;
  const validCount = parsedResult?.validUnique.length ?? 0;
  const dupCount = parsedResult?.duplicatesCount ?? 0;
  const unknownCount = parsedResult?.invalidCount ?? 0;

  return (
    <div className="sp-page" data-testid="skills-profile-item-selection">
      <div className="sp-page-header">
        <h1 className="sp-page-title">Item Selection</h1>
        <p className="sp-page-sub">
          Paste a list of session IDs and add them to an existing batch, or create a new one.
        </p>
      </div>

      {feedback && (
        <div className={`sp-alert sp-alert-${feedback.type}`} role="alert" data-testid="item-selection-feedback">
          <FontAwesomeIcon
            icon={
              feedback.type === "success"
                ? faCheckCircle
                : feedback.type === "warning"
                ? faExclamationTriangle
                : faExclamationCircle
            }
            style={{ marginTop: "3px" }}
          />
          <div>
            <div>{feedback.message}</div>
            {feedback.details && feedback.details.length > 0 && (
              <ul style={{ margin: "6px 0 0 0", paddingLeft: "18px" }}>
                {feedback.details.map((detail, idx) => (
                  <li key={idx}>{detail}</li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}

      <div className="sp-two-col">
        {/* Main Column: Textarea & Preview */}
        <div className="sp-col-main">
          <div className="sp-card">
            <div className="sp-card-title">Session IDs</div>
            <div className="sp-field">
              <label htmlFor="id-textarea" className="sp-field-label">
                Paste one session ID per line
              </label>
              <textarea
                id="id-textarea"
                data-testid="session-ids-textarea"
                className="sp-control sp-id-textarea"
                placeholder="a1e4c2d0-9f2b-4a11-8e3a-0d2f6b1c9a44&#10;c39b6a02-7e1d-4f88-9a2c-51e0d8f4b103"
                value={rawText}
                onChange={(e) => setRawText(e.target.value)}
              />
            </div>
            <div style={{ marginTop: "12px" }}>
              <button
                type="button"
                id="parse-btn"
                data-testid="parse-btn"
                className="sp-btn sp-btn-dark"
                onClick={handleParse}
                disabled={!rawText.trim()}
              >
                Parse IDs
              </button>
            </div>

            {parsedResult && parsedResult.items.length > 0 && (
              <table className="sp-id-preview" data-testid="id-preview-table">
                <thead>
                  <tr>
                    <th>Session ID</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {parsedResult.items.map((item, idx) => (
                    <tr key={idx} data-testid={`preview-row-${idx}`}>
                      <td>{item.id}</td>
                      <td>
                        <span
                          className={`sp-status-tag sp-status-${
                            item.status === "valid"
                              ? "valid"
                              : item.status === "dup"
                              ? "dup"
                              : "unknown"
                          }`}
                        >
                          {item.status === "valid"
                            ? "Valid"
                            : item.status === "dup"
                            ? "Duplicate"
                            : "Unrecognized"}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>

        {/* Side Column: Validation Stats & Target Batch */}
        <div className="sp-col-side">
          <div className="sp-card" style={{ marginBottom: "16px" }}>
            <div className="sp-card-title">Validation</div>
            {!parsedResult ? (
              <div className="sp-hint" data-testid="validation-empty">
                Parse a list to see counts here.
              </div>
            ) : (
              <div data-testid="validation-stats">
                <div className="sp-validation-stat">
                  <span>Parsed</span>
                  <span className="sp-num" data-testid="stat-parsed">
                    {parsedCount}
                  </span>
                </div>
                <div className="sp-validation-stat">
                  <span>Valid, unique</span>
                  <span className="sp-num sp-num-ok" data-testid="stat-valid">
                    {validCount}
                  </span>
                </div>
                <div className="sp-validation-stat">
                  <span>Duplicates</span>
                  <span className="sp-num sp-num-warn" data-testid="stat-dupe">
                    {dupCount}
                  </span>
                </div>
                <div className="sp-validation-stat">
                  <span>Unrecognized format</span>
                  <span className="sp-num sp-num-warn" data-testid="stat-unknown">
                    {unknownCount}
                  </span>
                </div>
              </div>
            )}
          </div>

          <div className="sp-card">
            <div className="sp-card-title">Add to batch</div>
            <form onSubmit={handleSubmit}>
              <div className="sp-radio-row">
                <input
                  type="radio"
                  name="batch-target"
                  id="target-existing"
                  data-testid="target-existing-radio"
                  checked={batchTarget === "existing"}
                  onChange={() => setBatchTarget("existing")}
                  disabled={batches.length === 0}
                />
                <label htmlFor="target-existing">Add to existing batch</label>
              </div>

              <select
                id="existing-batch-select"
                data-testid="existing-batch-select"
                className="sp-control"
                style={{ width: "100%", marginBottom: "14px" }}
                value={selectedBatchId}
                onChange={(e) => setSelectedBatchId(e.target.value)}
                disabled={batchTarget !== "existing" || loadingBatches || batches.length === 0}
              >
                {batches.length === 0 ? (
                  <option value="">No batches available</option>
                ) : (
                  batches.map((b) => (
                    <option key={b.id} value={b.id}>
                      {b.name} ({b.session_count || 0} sessions)
                    </option>
                  ))
                )}
              </select>

              <div className="sp-radio-row">
                <input
                  type="radio"
                  name="batch-target"
                  id="target-new"
                  data-testid="target-new-radio"
                  checked={batchTarget === "new"}
                  onChange={() => setBatchTarget("new")}
                />
                <label htmlFor="target-new">Create new batch</label>
              </div>

              <input
                id="new-batch-name"
                data-testid="new-batch-name-input"
                className="sp-control"
                placeholder="New batch name…"
                style={{ width: "100%", marginBottom: "16px" }}
                value={newBatchName}
                onChange={(e) => setNewBatchName(e.target.value)}
                disabled={batchTarget !== "new"}
              />

              <button
                type="submit"
                id="submit-batch-btn"
                data-testid="submit-batch-btn"
                className="sp-btn sp-btn-primary"
                style={{ width: "100%", justifyContent: "center" }}
                disabled={
                  submitting ||
                  validCount === 0 ||
                  (batchTarget === "new" && !newBatchName.trim()) ||
                  (batchTarget === "existing" && !selectedBatchId)
                }
              >
                {submitting ? (
                  <>
                    <FontAwesomeIcon icon={faSpinner} spin /> Adding sessions…
                  </>
                ) : (
                  "Add sessions to batch"
                )}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};
